import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";

import LoadingSpinner from "../../components/loadSpinner/LoadingSpinner.jsx";
import { getSessionItem } from "../../utils/helper/session.js";
import { validateTeamMemberAccess } from "../../utils/helper/teamMemberAccess.js";

/**
 * Protects routes whose employeeId must belong to the
 * authenticated manager's team.
 *
 * Admin users bypass the team-member validation.
 *
 * @returns {JSX.Element}
 */
function TeamMemberProtectedRoute() {
  const { employeeId } = useParams();
  const navigate = useNavigate();

  const toastRef = useRef(null);
  const redirectTimeoutRef = useRef(null);

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  const sessionUser = getSessionItem("User");
  const managerId = Number(sessionUser?.id);
  const userRole = sessionUser?.roleName;
  const isAdmin = userRole === "VP" || userRole === "CEO" || userRole === "HR";

  useEffect(() => {
    let isMounted = true;

    const redirectToSummary = (message) => {
      if (!isMounted) return;

      setIsAuthorized(false);
      setIsValidating(false);

      toastRef.current?.show({
        severity: "warn",
        summary: "Access denied",
        detail: message,
        life: 2500,
      });

      redirectTimeoutRef.current = setTimeout(() => {
        if (isMounted) {
          navigate("/summary", { replace: true });
        }
      }, 2500);
    };

    const validateAccess = async () => {
      setIsAuthorized(false);
      setIsValidating(true);

      // Admin users can access any employee without team validation.
      if (isAdmin) {
        if (isMounted) {
          setIsAuthorized(true);
          setIsValidating(false);
        }

        return;
      }

      const requestedEmployeeId = Number(employeeId);

      if (
        !Number.isInteger(requestedEmployeeId) ||
        requestedEmployeeId <= 0
      ) {
        redirectToSummary(
          "The provided employee ID is invalid. You will be redirected."
        );
        return;
      }

      if (!Number.isInteger(managerId) || managerId <= 0) {
        redirectToSummary(
          "Your session could not be identified. You will be redirected."
        );
        return;
      }

      try {
        const hasAccess = await validateTeamMemberAccess(
          managerId,
          requestedEmployeeId
        );

        if (!isMounted) return;

        if (!hasAccess) {
          redirectToSummary(
            "You do not have permission to access this user's information. You will be redirected."
          );
          return;
        }

        setIsAuthorized(true);
        setIsValidating(false);
      } catch (error) {
        console.error("Error validating team member access:", error);

        redirectToSummary(
          "We could not validate your access. You will be redirected."
        );
      }
    };

    validateAccess();

    return () => {
      isMounted = false;

      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [employeeId, managerId, isAdmin, navigate]);

  return (
    <>
      <Toast ref={toastRef} position="top-center" />
      {isValidating && <LoadingSpinner />}
      {!isValidating && isAuthorized && <Outlet />}
    </>
  );
}

export default TeamMemberProtectedRoute;